<template>
  <div class="relative  px-5 flex  justify-between  flex-col gap-5 pb-5 bg-white rounded-lg shadow-lg overflow-hidden">
    <!-- Confetti Canvas -->
    <canvas ref="confettiCanvas" class="absolute w-full h-full inset-0 pointer-events-none"></canvas>

    <!-- Wobbling Title -->
    <!--
    <h2 v-if="showTitle" class="absolute top-5 left-1/2 z-10 text-4xl text-green-700 animate-shake">
      Abrakadabra!
    </h2>
    -->

    <p>
      Mit unserem <strong>Abrakadabra-Feature</strong> beheben Sie lästige IT-Kleinigkeiten im Handumdrehen – ganz ohne Aufwand für Ihre IT-Abteilung. Prüfen Sie einfach, ob eines der unten aufgeführten Probleme auftritt, und lösen Sie es mit einem Klick auf <strong>„Problemlösung starten"</strong>!
    </p>

    <!-- Loading overlay when processing -->
    <div v-if="isLoading" class="absolute inset-0 bg-white flex flex-col items-center justify-center z-20">
      <div class="animate-spin h-12 w-12 border-4 border-blue-500 rounded-full border-t-transparent mb-4"></div>
      <p class="text-xl font-medium text-center text-black">{{ currentLoadingMessage }}</p>
    </div>

    <!-- Success message -->
    <div v-if="showSuccessMessage" class="w-full p-4 bg-green-100  text-green-700 border border-green-300 rounded-lg mb-4 text-center">
      <p class="text-lg font-medium ">Abrakadabra wurde erfolgreich ausgeführt!</p>
      <p>Hoffentlich ist Ihr Problem jetzt gelöst. Falls nicht, versuchen Sie es erneut oder wenden Sie sich an Ihre IT-Abteilung.</p>
    </div>

    <!-- Error message -->
    <div v-if="showErrorMessage" class="w-full p-4 bg-red-100 text-red-700 border border-red-300 rounded-lg mb-4 text-center">
      <p class="text-lg font-medium ">Hier hilft nicht einmal mehr Magie!</p>
      <p>Dieses Problem kann nur Ihre IT-Abteilung lösen. Bitte wenden Sie sich an den Support.</p>
    </div>

    <div class="w-full flex-grow">
      <table class="w-full table-auto text-left">
        <thead>
        <tr class="text-white">
          <th class="w-4/6">Problem</th>
          <th class="w-2/6">Aktion</th>
        </tr>
        </thead>
        <tbody>
        <tr
            v-for="(problem, idx) in problems"
            :key="problem.id"
        >
          <td class="">{{ idx + 1 }}. {{ problem.description }}</td>
          <td class="">
            <button
                @click="handleSolve(problem.problemType)"
                :class="['p-2 w-full', 'bg-trader-green text-trader-dark-green hover:bg-trader-hover-background hover:text-trader-hover-text']"
                :disabled="isLoading"
            >
              Problemlösung starten
            </button>
          </td>
        </tr>
        </tbody>
      </table>
    </div>

    <div class="w-full flex gap-2 mt-4">
      <button
          @click="close"
          data-ui="close-abracadabra"
          class="bg-trader-gray w-full p-2 hover:bg-trader-hover-background hover:text-trader-hover-text"
          :disabled="isLoading"
      >
        Schließen
      </button>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import apolloClient from '@/apolloClient'
import { gql } from '@apollo/client/core'
import { eventBus } from '@/application/EventBus'
import confetti from 'canvas-confetti'
import {mutation} from "@/graphql";

// Props
const props = defineProps({
  pageId: {
    type: Number,
    required: true,
  },
  reloadPage: {
    type: Function,
    default: () => {},
  },
})

// Reactive State
const problems = ref([
  { problemType: 'product', description: 'Der Artikel kann nicht platziert werden oder befindet sich angeblich schon auf der Seite.' },
  { problemType: 'cache', description: 'Produktinformationen oder Vorschauen sind nicht aktuell.' },
  { problemType: 'duplicate_products', description: 'Ein oder mehrere Produkte sind doppelt auf der Seite zu sehen.' },
])

const showTitle = ref(false)
const isLoading = ref(false)
const showSuccessMessage = ref(false)
const showErrorMessage = ref(false)
const currentLoadingMessage = ref('')
let myConfetti
const confettiCanvas = ref(null)

// Magic-themed loading messages
const loadingMessages = [
  "Die magischen Zauberformeln werden gesprochen...",
  "Wir schwingen gerade den Zauberstab...",
  "Die magischen Elfen arbeiten an Ihrer Lösung...",
  "Hokus Pokus Fidibus...",
  "Simsalabim, gleich ist es soweit...",
  "Magische Kräfte werden gebündelt...",
  "Der Zaubertrank köchelt...",
  "Ein magischer Wirbel beseitigt das Problem...",
  "Die Kristallkugel wird befragt...",
  "Die Sterne werden ausgerichtet..."
]


// Methods
function getRandomLoadingMessage() {
  const randomIndex = Math.floor(Math.random() * loadingMessages.length)
  return loadingMessages[randomIndex]
}

function updateLoadingMessage() {
  if (isLoading.value) {
    currentLoadingMessage.value = getRandomLoadingMessage()
    setTimeout(updateLoadingMessage, 2000) // Change message every 2 seconds
  }
}

const handleSolve = async (problemType) => {
  // Reset any previous messages
  showSuccessMessage.value = false
  showErrorMessage.value = false

  // Start loading state
  isLoading.value = true
  currentLoadingMessage.value = getRandomLoadingMessage()
  updateLoadingMessage()


  const [result, error] = await mutation.runAbracadabra({
    problemType: problemType,
    pageId: props.pageId,
  });

  if (error) {
    console.error('Mutation failed:', error)
    handleError()
    return
  }

  handleSuccess()
}

function handleSuccess() {
  // Stop loading
  isLoading.value = false

  // Show success message
  showSuccessMessage.value = true

  // Show wobbling title
  showTitle.value = true
  setTimeout(() => {
    showTitle.value = false
  }, 2000)

  // Trigger confetti
  myConfetti({
    particleCount: 200,
    spread: 100,
    origin: { y: 0.8 },
  })

  // After success, you might want to reload the page
  setTimeout(() => {
    props.reloadPage()
  }, 1000)
}

function handleError() {
  // Stop loading
  isLoading.value = false

  // Show error message
  showErrorMessage.value = true
}

function close() {
  // Reset all states before closing
  isLoading.value = false
  showSuccessMessage.value = false
  showErrorMessage.value = false

  eventBus.closeModal()
}

// Lifecycle
onMounted(() => {
  // Initialize confetti on our custom canvas
  myConfetti = confetti.create(confettiCanvas.value, { resize: true })
})
</script>

<style scoped>
@keyframes zoom-spin {
  0% { transform: scale(1) rotate(0deg); }
  50% { transform: scale(1.1) rotate(10deg); }
  100% { transform: scale(1) rotate(0deg); }
}
.animate-zoom-spin {
  animation: zoom-spin 0.8s ease-out;
}

@keyframes shake {
  0%, 100% { transform: translateX(-50%); }
  10%, 30%, 50%, 70%, 90% { transform: translateX(calc(-50% - 5px)); }
  20%, 40%, 60%, 80% { transform: translateX(calc(-50% + 5px)); }
}
.animate-shake {
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  animation: shake 1.8s ease-in-out;
}
</style>